import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';

export const RATING_STAR_COUNT = 5;

const STAR_PATH =
  'M12 2.5l2.97 6.02 6.65.97-4.81 4.69 1.14 6.62L12 17.77 6.05 20.8l1.14-6.62L2.38 9.49l6.65-.97L12 2.5z';

interface RatingFieldProps {
  variableName: string;
  /** Optional filled-star color override. Defaults to gold. */
  customColor?: string;
  className?: string;
  ariaLabel?: string;
}

function parseStarCount(value: string): number {
  const trimmed = value.trim();
  if (/^[1-5]$/.test(trimmed)) {
    return Number(trimmed);
  }
  return 0;
}

function starLabel(stars: number): string {
  return stars === 1 ? '1 star' : `${stars} stars`;
}

/**
 * Horizontal 1–5 star rating bound to a string variable.
 * Empty string is unrated. Clicking star N fills 1..N. Clicking the only selected
 * first star again returns to unrated.
 */
const RatingField: React.FC<RatingFieldProps> = ({
  variableName,
  customColor,
  className,
  ariaLabel
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Optimistic value shown immediately on click; null means use store. Empty string is unrated. */
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null);
  const [hoveredStars, setHoveredStars] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [variableName]);

  const effectiveStored = useMemo(
    () => parseStarCount((storedValue ?? '').toString()),
    [storedValue]
  );

  useEffect(() => {
    if (optimisticValue !== null && (storedValue ?? '').toString().trim() === optimisticValue) {
      setOptimisticValue(null);
    }
  }, [storedValue, optimisticValue]);

  const currentStars =
    optimisticValue !== null ? parseStarCount(optimisticValue) : effectiveStored;
  const displayStars = hoveredStars > 0 ? hoveredStars : currentStars;

  useEffect(() => {
    setError(null);
  }, [storedValue]);

  const submitValue = useCallback(
    async (newValue: string) => {
      if (newValue === (storedValue ?? '').toString().trim()) return;

      try {
        if (!isMountedRef.current) {
          return;
        }
        setIsSaving(true);
        setError(null);
        await submitUserInput(variableName, newValue);
        if (!isMountedRef.current) {
          return;
        }
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to update value';
        setError(message);
        setOptimisticValue(null);
        uiStore.setError({
          code: 'RATING_ERROR',
          message
        });
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [variableName, storedValue, submitUserInput, uiStore]
  );

  const selectStars = useCallback(
    (stars: number) => {
      if (isSaving) return;
      if (stars === 1 && currentStars === 1) {
        setOptimisticValue('');
        void submitValue('');
        return;
      }
      const newValue = String(stars);
      setOptimisticValue(newValue);
      void submitValue(newValue);
    },
    [isSaving, currentStars, submitValue]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          selectStars(Math.min(RATING_STAR_COUNT, currentStars + 1 || 1));
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          if (currentStars <= 1) {
            if (currentStars === 1) {
              selectStars(1);
            }
          } else {
            selectStars(currentStars - 1);
          }
          break;
        case 'Home':
          e.preventDefault();
          if (currentStars !== 0) {
            setOptimisticValue('');
            void submitValue('');
          }
          break;
        case 'End':
          e.preventDefault();
          selectStars(RATING_STAR_COUNT);
          break;
        case '0':
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (currentStars !== 0) {
            setOptimisticValue('');
            void submitValue('');
          }
          break;
        default:
          if (/^[1-5]$/.test(e.key)) {
            e.preventDefault();
            selectStars(Number(e.key));
          }
          break;
      }
    },
    [currentStars, selectStars, submitValue]
  );

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-rating'];
    if (className) classes.push(className);
    if (error) classes.push('rich-text-rating--error');
    if (isSaving) classes.push('rich-text-rating--saving');
    return classes.join(' ');
  }, [className, error, isSaving]);

  const rootStyle = useMemo(
    () =>
      customColor
        ? ({ ['--rating-fill' as string]: customColor } as React.CSSProperties)
        : undefined,
    [customColor]
  );

  const groupLabel = ariaLabel ?? 'Rating';
  const valueText = currentStars === 0 ? 'unrated' : starLabel(currentStars);

  return (
    <div
      className={rootClassName}
      {...(rootStyle ? { style: rootStyle } : {})}
      data-variable={variableName}
      data-testid={`rating-${variableName}`}
      data-rating={currentStars === 0 ? 'unrated' : String(currentStars)}
    >
      <div
        className="rich-text-rating__stars"
        role="radiogroup"
        aria-label={groupLabel}
        aria-valuetext={valueText}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseLeave={() => setHoveredStars(0)}
      >
        {Array.from({ length: RATING_STAR_COUNT }, (_, index) => {
          const stars = index + 1;
          const filled = displayStars >= stars;
          const checked = currentStars === stars;
          return (
            <button
              key={stars}
              type="button"
              className={
                filled
                  ? 'rich-text-rating__star rich-text-rating__star--filled'
                  : 'rich-text-rating__star'
              }
              role="radio"
              aria-checked={checked}
              aria-label={starLabel(stars)}
              disabled={isSaving}
              tabIndex={-1}
              onMouseEnter={() => setHoveredStars(stars)}
              onClick={() => selectStars(stars)}
            >
              <svg
                className="rich-text-rating__icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path d={STAR_PATH} />
              </svg>
            </button>
          );
        })}
      </div>

      {error && (
        <span
          className="rich-text-rating__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`rating-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default RatingField;
